import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult
} from '@tanstack/react-query'
import { useLocale } from 'next-intl'

interface CreateInfiniteHookConfig<TParams, TData, TPageParam, TError = Error> {
  getQueryKey: (params: TParams) => QueryKey
  queryFn: (args: { params: TParams; pageParam: TPageParam }) => Promise<TData>
  /** See createQueryHook — locale-dependent responses need it in the key. */
  localized?: boolean
  initialPageParam: TPageParam
  getNextPageParam: (
    lastPage: TData,
    allPages: TData[],
    lastPageParam: TPageParam,
    allPageParams: TPageParam[]
  ) => TPageParam | undefined
  getEnabled?: (params: TParams) => boolean
  getOptions?: (
    params: TParams
  ) => Omit<
    UseInfiniteQueryOptions<
      TData,
      TError,
      InfiniteData<TData, TPageParam>,
      QueryKey,
      TPageParam
    >,
    'queryKey' | 'queryFn' | 'enabled' | 'initialPageParam' | 'getNextPageParam'
  >
}

export function createInfiniteHook<TParams, TData, TPageParam, TError = Error>(
  config: CreateInfiniteHookConfig<TParams, TData, TPageParam, TError>
) {
  function useBaseInfiniteQuery(
    params: TParams,
    keySuffix: QueryKey
  ): UseInfiniteQueryResult<InfiniteData<TData, TPageParam>, TError> {
    return useInfiniteQuery<
      TData,
      TError,
      InfiniteData<TData, TPageParam>,
      QueryKey,
      TPageParam
    >({
      queryKey: [...config.getQueryKey(params), ...keySuffix],
      queryFn: context =>
        config.queryFn({
          params,
          pageParam: context.pageParam as TPageParam
        }),
      enabled: config.getEnabled ? config.getEnabled(params) : true,
      initialPageParam: config.initialPageParam,
      getNextPageParam: config.getNextPageParam,
      ...(config.getOptions ? config.getOptions(params) : {})
    })
  }

  // See createQueryHook: only a localized hook depends on the intl provider.
  if (!config.localized) {
    return function useGeneratedInfiniteQuery(params: TParams) {
      return useBaseInfiniteQuery(params, [])
    }
  }

  return function useLocalizedInfiniteQuery(params: TParams) {
    return useBaseInfiniteQuery(params, [useLocale()])
  }
}
